/**
 * ConnectionHealthPanel — diagnóstico da PRÓPRIA conexão, dentro do modal.
 *
 * Por que existe: o modal dizia "Sua conta está ativa e recebendo mensagens"
 * baseado só em `connection_status` do nosso banco. No Gelo Americano essa
 * frase ficou verdadeira na tela por 17 dias enquanto a Meta não entregava
 * webhook nenhum. Estado salvo não é prova de saúde; aqui a gente pergunta de
 * verdade (Graph API, Evolution, e há quanto tempo o canal não recebe).
 *
 * Compartilhado de propósito: recebe `canal` e serve WhatsApp Business, QR,
 * Instagram e Messenger com a mesma leitura. Regra da matriz de canais: se o
 * usuário não sabe dizer em qual inbox está olhando, é core.
 *
 * As cores e os rótulos são os MESMOS do Painel de Plataforma
 * (PlatformHealthTab): quem der suporte vê a mesma linguagem nos dois lugares.
 * CINZA nunca conta como verde — significa "não deu para checar".
 *
 * Props:
 *  - canal    -> 'wa_business' | 'wa_personal' | 'instagram' | 'messenger'
 *  - colors   -> tema (useTheme)
 *  - compacto -> esconde os itens VERDES até o usuário pedir (padrão do modal)
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle2, HelpCircle,
  RefreshCw, Loader2, Stethoscope, ChevronDown, ChevronUp,
} from 'lucide-react';
import platformHealthService from '../../services/global/platformHealthService';

const NIVEIS = {
  VERMELHO: { cor: '#ea4335', rotulo: 'Problema', Icone: AlertCircle },
  AMBAR: { cor: '#f59e0b', rotulo: 'Atenção', Icone: AlertTriangle },
  CINZA: { cor: '#8696a0', rotulo: 'Não checado', Icone: HelpCircle },
  VERDE: { cor: '#00a884', rotulo: 'Ok', Icone: CheckCircle2 },
};
const nivelDe = (n) => NIVEIS[n] || NIVEIS.CINZA;

// Frase do topo. Nunca afirma que está recebendo quando não foi possível
// checar: a diferença entre "está ok" e "não sei" é a que salva o suporte.
const RESUMO = {
  VERMELHO: 'Encontramos um problema nesta conexão',
  AMBAR: 'A conexão funciona, mas há um ponto de atenção',
  CINZA: 'Não foi possível checar a conexão agora',
  VERDE: 'Conexão checada e funcionando',
};

const ConnectionHealthPanel = ({ canal, colors, compacto = true }) => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [mostrarTudo, setMostrarTudo] = useState(!compacto);

  const carregar = useCallback(async (refresh = false) => {
    setCarregando(true);
    setErro('');
    try {
      const r = await platformHealthService.getMinhaConexao(canal, refresh);
      setDados(r);
    } catch {
      setErro('Não foi possível checar a conexão agora.');
    } finally {
      setCarregando(false);
    }
  }, [canal]);

  useEffect(() => { carregar(false); }, [carregar]);

  if (carregando && !dados) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
        borderRadius: 10, border: `1px solid ${colors.borderColor}`,
        color: colors.textSecondary, fontSize: 13,
      }}>
        <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
        Checando a conexão...
      </div>
    );
  }

  if (erro || !dados) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
        borderRadius: 10, border: `1px solid ${colors.borderColor}`,
        color: colors.textSecondary, fontSize: 13,
      }}>
        <HelpCircle size={15} color={NIVEIS.CINZA.cor} />
        {erro || 'Sem diagnóstico disponível.'}
        <button
          onClick={() => carregar(true)}
          style={{
            marginLeft: 'auto', background: 'transparent', border: 'none',
            color: colors.accentPrimary, fontSize: 12, cursor: 'pointer', fontWeight: 600,
          }}
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  const checks = dados.checks || [];
  const geral = nivelDe(dados.nivel_geral);
  const problemas = checks.filter((c) => c.nivel !== 'VERDE');
  const visiveis = mostrarTudo ? checks : (problemas.length ? problemas : []);
  const verdes = checks.length - problemas.length;

  return (
    <div style={{
      borderRadius: 12, border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '11px 13px', borderBottom: visiveis.length ? `1px solid ${colors.borderColor}` : 'none',
      }}>
        <Stethoscope size={15} color={geral.cor} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
            {RESUMO[dados.nivel_geral] || RESUMO.CINZA}
          </div>
          <div style={{ fontSize: 11.5, color: colors.textTertiary }}>
            {checks.length} verificação(ões)
            {verdes > 0 && problemas.length > 0 ? `, ${verdes} ok` : ''}
          </div>
        </div>
        <button
          onClick={() => carregar(true)}
          disabled={carregando}
          title="Checar de novo agora"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: colors.textTertiary, display: 'inline-flex', padding: 4,
          }}
        >
          {carregando
            ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            : <RefreshCw size={14} />}
        </button>
      </div>

      {visiveis.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {visiveis.map((c, i) => {
            const n = nivelDe(c.nivel);
            const Icone = n.Icone;
            return (
              <div
                key={`${c.item}-${i}`}
                style={{
                  display: 'flex', gap: 9, padding: '10px 13px',
                  borderTop: i ? `1px solid ${colors.borderColor}55` : 'none',
                }}
              >
                <Icone size={14} color={n.cor} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: colors.textPrimary }}>
                    {c.item}
                  </div>
                  <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
                    {c.detalhe}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {checks.length > 0 && (
        <button
          onClick={() => setMostrarTudo((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            width: '100%', padding: '8px 0', border: 'none',
            borderTop: `1px solid ${colors.borderColor}55`,
            background: 'transparent', color: colors.textTertiary,
            fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {mostrarTudo
            ? <>Mostrar só o que precisa de atenção <ChevronUp size={12} /></>
            : <>Ver todas as verificações <ChevronDown size={12} /></>}
        </button>
      )}
    </div>
  );
};

export default ConnectionHealthPanel;
